import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Retencion, IRetencionPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchRetenciones = ({
  page = 1,
  page_size = 10,
  codigo = '',
}: GetRetencionesParams) => {
  return useQuery({
    queryKey: ['Retenciones', page, codigo, page_size],
    queryFn: async () => {
      return getRetenciones({ page, codigo, page_size });
    },
  });
};

export const useGetRetencion = (id: string) => {
  return useQuery({
    queryKey: ['Retenciones', id],
    queryFn: async () => {
      return getRetencion(id);
    },
  });
};

export const useCreateRetencion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createRetencion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Retenciones'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Retencion creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};


export const useUpdateRetencion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateRetencion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Retenciones'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Retenciones actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetRetencionesParams = {
  page?: number;
  page_size?: number;
  codigo?: string;
};

export type CreateRetencionParams = Omit<Retencion, 'id_retencion'>;
export type UpdateRetencionParams = {
  id: string;
  data: CreateRetencionParams;
};

export const getRetenciones = async (params?: GetRetencionesParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<IRetencionPagingRes>(
    `${urlBackend}api/v1/retencion/?${queryParams}`,
    true
  );
};

export const getRetencion = async (id: string) => {
  try{
    return await get<Retencion>(`${urlBackend}api/v1/retencion/${id}`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createRetencion = async (data: CreateRetencionParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Retencion>(`${urlBackend}api/v1/retencion/`, data, true);
};

export const updateRetencion = async ({ id, data }: UpdateRetencionParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Retencion>(`${urlBackend}api/v1/retencion/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};
